import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_CONST } from '../const';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return { Authorization: `Bearer ${token}` };
};

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (limit = 20, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_CONST.NOTIFICATIONS}?limit=${limit}`, {
        headers: getAuthHeaders(),
      });
      return res.data.notifications;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load notifications');
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(API_CONST.NOTIFICATIONS_UNREAD_COUNT, {
        headers: getAuthHeaders(),
      });
      return res.data.count;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load unread count');
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'notifications/markRead',
  async (id, { rejectWithValue }) => {
    try {
      await axios.patch(
        API_CONST.NOTIFICATIONS_MARK_READ.replace(':id', id),
        {},
        { headers: getAuthHeaders() }
      );
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to mark as read');
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'notifications/markAllRead',
  async (_, { rejectWithValue }) => {
    try {
      await axios.patch(API_CONST.NOTIFICATIONS_READ_ALL, {}, { headers: getAuthHeaders() });
      return true;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to mark all as read');
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState: {
    notifications: [],
    unreadCount: 0,
    loading: false,
    error: null,
  },
  reducers: {
    resetNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const id = action.payload;
        const notif = state.notifications.find((n) => n.id === id);
        if (notif && !notif.readAt) {
          notif.readAt = new Date().toISOString();
          if (state.unreadCount > 0) state.unreadCount -= 1;
        }
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.notifications.forEach((n) => {
          if (!n.readAt) n.readAt = new Date().toISOString();
        });
        state.unreadCount = 0;
      });
  },
});

export const { resetNotifications } = notificationSlice.actions;
export default notificationSlice.reducer;
